import React, { useEffect, useState, useMemo, useRef, memo } from 'react'
import ReactDOM from 'react-dom'
import moment from 'moment'
import { Group } from '@vx/group'

import { Legend } from '../../common/legend/legend'
import { TooltipContextProvider } from '../../common/tooltip/tooltip.context'
import { Tooltip, TOOLTIP_TYPE } from '../../common/tooltip/tooltip'
import { Axes } from '../../common/axes/axes'

import { lineChartService } from './line-chart.service'
import { MemoizedLinePaths as LinePaths } from './line-paths'
import { HoverMarkers } from './hover-markers'
import { LineMarker } from './line-marker'
import { getAxesFormatters } from './format.service'

import { colorService } from '../../common/color.service'

import { useStyles } from './line.style'

export function LineChartContainer (props = {}) {
  const containerRef = useRef()
  const { children, defaultLegendToggles, series = [], xFormat, yFormat, xValueDomain, yValueDomain, timeFormat, width, height } = props
  const [colors, setColors] = useState([])
  const [legendToggles, setLegendToggles] = useState()
  const nextColorIndex = useRef(0)
  const seriesColorCache = useRef({})

  useEffect(() => {
    setLegendToggles(defaultLegendToggles || {})
  }, [ defaultLegendToggles ])

  useEffect(() => {
    setSeriesColors()
  }, [])

  useEffect(() => {
    setSeriesColors()
  }, [series, legendToggles, xFormat, yFormat, xValueDomain, yValueDomain, timeFormat, width, height])

  function setSeriesColors () {
    // Grab the colors that are already set
    const usedColors = []
    series.forEach(({ color }) => {
      if (color) {
        usedColors.push(color)
      }
    })

    // Set colors for any series that are not already set
    series.forEach((s) => {
      if (!s.color) {
        const { color, nextIndex } = colorService.pickNextBestColor({
          usedColors,
          cachedColor: seriesColorCache.current[s.id],
          nextIndex: nextColorIndex.current
        })
        s.color = color
        usedColors.push(color)
        seriesColorCache.current[s.id] = color
        nextColorIndex.current = nextIndex
      }
    })
    setColors(series.map((s) => s.color))
  }

  return (
    <div ref={containerRef}>
      {children({
        colors,
        legendToggles,
        onLegendToggle: setLegendToggles,
        containerRef
      })}
    </div>
  )
}

const _SingleLineChart = function ({
  containerRef,
  width = 1024,
  height = 500,
  series,
  axisMatchSeries = [],
  axisLabels = { x: '', y: '' },
  isTimeSeries,
  timeFormat,
  relativeTimeUnit,
  relativeTimeIndexDate,
  xFormat,
  yFormat,
  y2Format,
  yCIFormat,
  xValueDomain,
  yValueDomain,
  xTickValues = 'auto',
  curve,
  showPoints,
  missingPointDisplay,
  markers = [],
  showHorizontalLineAtZero = false,
  onClick = function () {},
  legendToggles,
  onHover = function () {},
  onPointUpdate = function () {},
  currentHover,
  colors = [],
  marginTop = 40,
  marginRight = 40,
  marginBottom = 55,
  marginLeft = 75
}) {
  const classes = useStyles()

  const svg = { width, height }
  width = width - marginLeft - marginRight
  height = height - marginTop - marginBottom

  const [seriesToRender, setSeriesToRender] = useState()
  const [distinctValues, setDistinctValues] = useState()
  const [scales, setScales] = useState()
  const [axesFormatters, setAxesFormatters] = useState()
  const [mouseXPosition, setMouseXPosition] = useState()
  const labels = useRef([])

  useEffect(() => {
    if (!series || series.length === 0) {
      setSeriesToRender(series)
      return
    }

    const transformedSeries = [
      ...series,
      ...(axisMatchSeries.map((s) => ({ ...s, isAxisMatchSeries: true })))
    ]

    transformedSeries.forEach((s) => {
      s.bands = lineChartService.getBands(s.points, s.bandNames, s.color)

      if (isTimeSeries) {
        s.points.forEach((p) => {
          if (!(p.x instanceof Date)) {
            p.x = moment.utc(p.x.toString()).toDate()
          }
        })
      }
    })

    const seriesToRender = transformedSeries.filter((s) => !s.isAxisMatchSeries)

    const distinctValues = lineChartService.getDistinctValues(seriesToRender, { seriesToHide: legendToggles })
    setDistinctValues(distinctValues)

    if (isTimeSeries && xValueDomain && xValueDomain.length && !(xValueDomain[0] instanceof Date)) {
      xValueDomain = xValueDomain.map((x) => moment.utc(x).toDate())
    }

    const scales = lineChartService.calculateScales(transformedSeries, { width, height }, isTimeSeries, { x: xValueDomain, y: yValueDomain }, legendToggles)
    setScales(scales)

    // Use the first x value if we're displaying relative time with no index date specified
    if (relativeTimeUnit && !relativeTimeIndexDate) {
      relativeTimeIndexDate = scales.x.domain()[0]
    }

    setAxesFormatters(getAxesFormatters({ isTimeSeries, timeFormat, relativeTimeUnit, relativeTimeIndexDate, xFormat, yFormat }))
    setSeriesToRender(seriesToRender)
  }, [ series, legendToggles, xFormat, yFormat, xValueDomain, yValueDomain, timeFormat, width, height ])

  function handleMousePosChange (xPosition) {
    setMouseXPosition(xPosition - marginLeft)
  }

  // Tranform x tick values to dates if specific values provided for a time series
  if (Array.isArray(xTickValues) && isTimeSeries) {
    xTickValues = xTickValues.map((x) => new Date(x))
  }
  const lineAxes = useMemo(() => scales ? (
    <Axes
      chartRef={containerRef}
      scales={scales}
      labels={axisLabels}
      dimensions={{ width, height }}
      formatters={axesFormatters}
      // If array passed, use those values, if 'all' send all the distinct values, otherwise it's 'auto' so let D3 decide
      tickValues={{ x: Array.isArray(xTickValues) ? xTickValues : (xTickValues === 'all') ? distinctValues.x : null }}
    />
  ) : null, [ scales, width, height, axesFormatters, distinctValues, xTickValues, axisLabels ])

  if (!seriesToRender || seriesToRender.length === 0 || !scales || !distinctValues) {
    // This prevents the line chart from causing shifts in content when it is loaded the first time.
    return <div style={{ width: svg.width, height: svg.height }} />
  }

  labels.current = seriesToRender.map((s) => (s.label || s.name))
  seriesToRender.forEach((s, i) => {
    s.color = colors[i]
  })

  return (
    <svg className={classes.svg} width={svg.width} height={svg.height}>
      <Group transform={`translate(${marginLeft}, ${marginTop})`}>
        {showHorizontalLineAtZero &&
          <line
            y1={scales.y(0)}
            y2={scales.y(0)}
            stroke="gray"
            strokeWidth={0.5}
            x1={0}
            x2={width}
          />
        }
        {lineAxes}
        <LinePaths series={seriesToRender} scales={scales} curve={curve} showPoints={showPoints} seriesToHide={legendToggles} />
        <HoverMarkers
          series={seriesToRender}
          scales={scales}
          dimensions={{ width, height }}
          svgMargin={marginLeft}
          formatters={axesFormatters}
          yFormat={yFormat}
          y2Format={y2Format}
          yCIFormat={yCIFormat}
          seriesToHide={legendToggles}
          missingPointDisplay={missingPointDisplay}
          onMousePosChange={handleMousePosChange}
          onClick={onClick}
          onHover={onHover}
          onPointUpdate={onPointUpdate}
          currentHover={currentHover}
        />
        {markers.map((marker, index) => {
          const markerX = isTimeSeries ? moment.utc(marker.x).toDate() : marker.x
          return (
            <LineMarker key={index} label={marker.label} verticalMarkerAt={markerX} scales={scales} height={height} mouseXPosition={mouseXPosition} padding={marker.padding} />
          )
        })}
      </Group>
    </svg>
  )
}

export function LineChart (props = {}) {
  const ref = useRef()

  return (
    <div ref={ref}>
      <LineChartContainer {...props}>
        {({ colors, legendToggles, onLegendToggle, containerRef }) => (
          <>
            <Legend labels={(props.series || []).map((s) => (s.label || s.name))} colors={colors} toggleDefaults={legendToggles} onToggle={onLegendToggle} />
            <TooltipContextProvider>
              <SingleLineChart colors={colors} containerRef={containerRef} legendToggles={legendToggles} {...props} />
              <Tooltip type={TOOLTIP_TYPE.LIST} />
            </TooltipContextProvider>
          </>
        )}
      </LineChartContainer>
    </div>
  )
}

export async function lineWidget (id, props) {
  const { series } = props

  // Convert naming convensions from R to JS
  series.forEach((s) => {
    s.bandNames = s['band_names']
  })

  const element = document.getElementById(id)
  ReactDOM.render(
    React.createElement(LineChart, props),
    element
  )
}

export const SingleLineChart = memo(_SingleLineChart)
